// OSIS - Standarde și Norme
import React, { useState } from 'react';
import { 
  BookOpen,
  FileText,
  Search,
  Download,
  ExternalLink,
  Star,
  Calendar,
  Filter,
  Tag,
  Globe,
  Shield,
  Thermometer,
  Wind,
  Zap,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye
} from 'lucide-react';
import Card from '../../components/Card';

const StandardsLibrary = () => {
  const [selectedCategory, setSelectedCategory] = useState('toate');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('toate');

  // Categorii de standarde
  const categories = [
    { id: 'toate', name: 'Toate standardele', count: 89, icon: BookOpen },
    { id: 'structurale', name: 'Calcule Structurale', count: 15, icon: Shield },
    { id: 'termice', name: 'Performanțe Termice', count: 12, icon: Thermometer },
    { id: 'climatice', name: 'Sarcini Climatice', count: 8, icon: Wind },
    { id: 'seismice', name: 'Rezistență Seismică', count: 6, icon: Zap },
    { id: 'securitate', name: 'Securitate și Efracție', count: 10, icon: Shield },
    { id: 'calitate', name: 'Control Calitate', count: 18, icon: CheckCircle },
    { id: 'instalare', name: 'Montaj și Instalare', count: 12, icon: Activity },
    { id: 'materiale', name: 'Materiale și Produse', count: 8, icon: Tag }
  ];

  // Regiuni/Organisme de standardizare
  const regions = [
    { id: 'toate', name: 'Toate regiunile', count: 89 },
    { id: 'romania', name: 'România (SR)', count: 28 },
    { id: 'european', name: 'European (EN)', count: 35 },
    { id: 'iso', name: 'International (ISO)', count: 16 },
    { id: 'german', name: 'Germania (DIN)', count: 10 }
  ];

  // Biblioteca de standarde
  const standards = [
    {
      id: 'SR-EN-1991-1-4',
      title: 'SR EN 1991-1-4:2005',
      fullName: 'Eurocod 1: Acțiuni asupra structurilor. Partea 1-4: Acțiuni generale. Acțiuni ale vântului',
      category: 'climatice',
      region: 'romania',
      organization: 'ASRO',
      status: 'Activă',
      publishDate: '2005-09-15',
      lastUpdate: '2019-03-12',
      language: 'Română',
      pages: 164,
      description: 'Standardul definește acțiunile vântului pentru calculul structural al clădirilor și structurilor. Include proceduri pentru determinarea presiunii vântului pe fațade și elemente.',
      applicableFor: ['suprafețe mari', 'Fațade continua', 'lucrări mari', 'Structuri expuse'],
      keyPoints: [
        'Hărți de zonare climatică România',
        'Coeficienți de presiune pentru diferite forme',
        'Calcularea efectelor dinamice ale vântului',
        'Proceduri pentru structuri înalte'
      ],
      relatedStandards: ['NP 082-04', 'SR EN 1990', 'SR EN 1993-1-1'],
      downloadUrl: '/standards/sr-en-1991-1-4.pdf',
      bookmarks: 24,
      downloads: 156,
      rating: 4.8,
      complexity: 'Complexă',
      priority: 'Ridicată'
    },
    {
      id: 'SR-EN-14351-1',
      title: 'SR EN 14351-1:2006',
      fullName: 'pardoseli industriale - Norma de produs, caracteristici de performanță. Partea 1: pardoseli industriale exterioare',
      category: 'calitate',
      region: 'romania',
      organization: 'ASRO',
      status: 'Activă',
      publishDate: '2006-11-20',
      lastUpdate: '2016-07-15',
      language: 'Română',
      pages: 98,
      description: 'Definește cerințele de performanță pentru pardoseli industriale exterioare, incluzând rezistența la vânt, impermeabilitatea la apă și aer, și proprietățile termice.',
      applicableFor: ['Toate proiectele', 'lucrări exterioare', 'Controlul calității', 'Certificare produse'],
      keyPoints: [
        'Clasificarea performanțelor A, B, C',
        'Metode de încercare standardizate',
        'Cerințe pentru impermeabilitate',
        'Criterii de acceptanță pentru testare'
      ],
      relatedStandards: ['SR EN 12207', 'SR EN 12208', 'SR EN 12210'],
      downloadUrl: '/standards/sr-en-14351-1.pdf',
      bookmarks: 31,
      downloads: 203,
      rating: 4.9,
      complexity: 'Medie',
      priority: 'Ridicată'
    },
    {
      id: 'SR-EN-1627',
      title: 'SR EN 1627-1630:2011',
      fullName: 'lucrări, proiecte, fațade, graje - Rezistența la efracție - Clasificare și metode de încercare',
      category: 'securitate',
      region: 'romania',
      organization: 'ASRO',
      status: 'Activă',
      publishDate: '2011-04-08',
      lastUpdate: '2018-11-22',
      language: 'Română',
      pages: 127,
      description: 'Standardul definește clasele de rezistență la efracție (RC1-RC6) și metodele de testare pentru lucrări și proiecte de securitate.',
      applicableFor: ['lucrări blindate', 'proiecte securizate', 'Sisteme acces controlat', 'Zone sensibile'],
      keyPoints: [
        'Clasificare RC1 până la RC6',
        'Timpul de rezistență la efracție',
        'Unelte standard de testare',
        'Cerințe pentru materiale și Materiale'
      ],
      relatedStandards: ['SR EN 356', 'SR EN 1303', 'SR EN 12209'],
      downloadUrl: '/standards/sr-en-1627-1630.pdf',
      bookmarks: 18,
      downloads: 89,
      rating: 4.7,
      complexity: 'Medie',
      priority: 'Medie'
    },
    {
      id: 'C107-2015',
      title: 'C 107/7-2015',
      fullName: 'Normativ privind calculul termotehnic al elementelor de construcție ale clădirilor',
      category: 'termice',
      region: 'romania',
      organization: 'MDRAP',
      status: 'Activă',
      publishDate: '2015-12-15',
      lastUpdate: '2020-08-10',
      language: 'Română',
      pages: 156,
      description: 'Normativul românesc pentru calculul performanțelor termice ale elementelor de construcție, inclusiv pardoseli industriale.',
      applicableFor: ['Certificat energetic', 'Eficiența energetică', 'proiecte pardoseală', 'lucrări izolante'],
      keyPoints: [
        'Calculul coeficientului U',
        'Zone climatice România',
        'Cerințe minime pentru proiecte',
        'Poduri termice și condensul'
      ],
      relatedStandards: ['SR EN ISO 6946', 'SR EN ISO 10077', 'C107-2005'],
      downloadUrl: '/standards/c107-7-2015.pdf',
      bookmarks: 45,
      downloads: 267,
      rating: 4.6,
      complexity: 'Medie',
      priority: 'Ridicată'
    },
    {
      id: 'P100-1-2013',
      title: 'P100-1/2013',
      fullName: 'Cod de proiectare seismică - Partea I - Prevederi de proiectare pentru clădiri',
      category: 'seismice',
      region: 'romania',
      organization: 'MDRAP',
      status: 'Activă',
      publishDate: '2013-07-12',
      lastUpdate: '2019-05-30',
      language: 'Română',
      pages: 234,
      description: 'Codul seismic românesc care definește cerințele pentru proiectarea antiseismică a construcțiilor, inclusiv elementele de fațadă.',
      applicableFor: ['Fațade mari', 'suprafețe mari', 'Sisteme curtain wall', 'Zone seismice'],
      keyPoints: [
        'Zonarea seismică a României',
        'Calculul accelerațiilor seismice',
        'Detalii constructive antiseismice',
        'Verificări specifice pentru fațade'
      ],
      relatedStandards: ['SR EN 1998', 'NP-112-04', 'SR EN 13830'],
      downloadUrl: '/standards/p100-1-2013.pdf',
      bookmarks: 12,
      downloads: 78,
      rating: 4.5,
      complexity: 'Complexă',
      priority: 'Medie'
    },
    {
      id: 'SR-EN-13830',
      title: 'SR EN 13830:2015',
      fullName: 'Fațade continua (curtain walling) - Norma de produs',
      category: 'structurale',
      region: 'romania',
      organization: 'ASRO',
      status: 'Activă',
      publishDate: '2015-03-25',
      lastUpdate: '2020-11-18',
      language: 'Română',
      pages: 89,
      description: 'Standardul pentru sistemele de fațadă continuă, definind cerințele de performanță și metodele de încercare pentru curtain wall systems.',
      applicableFor: ['Curtain wall', 'Fațade structurale', 'Clădiri înalte', 'Sisteme glazing'],
      keyPoints: [
        'Sisteme structurale vs non-structurale',
        'Testarea la presiune și depresiune',
        'Comportament termic și acustic',
        'Instalarea și fixarea panourilor'
      ],
      relatedStandards: ['SR EN 1991-1-4', 'SR EN 1993', 'SR EN 1999'],
      downloadUrl: '/standards/sr-en-13830.pdf',
      bookmarks: 8,
      downloads: 45,
      rating: 4.4,
      complexity: 'Complexă',
      priority: 'Scăzută'
    }
  ];

  // Statistici
  const stats = [
    {
      title: 'Total Standarde',
      value: '89',
      change: 'Actualizate periodic',
      changeType: 'neutral',
      icon: BookOpen,
      color: 'primary'
    },
    {
      title: 'Standarde Active',
      value: '84',
      change: '94% din total',
      changeType: 'positive',
      icon: CheckCircle,
      color: 'success'
    },
    {
      title: 'Actualizări Recente',
      value: '7',
      change: 'Ultima lună',
      changeType: 'neutral',
      icon: Clock,
      color: 'warning'
    },
    {
      title: 'Descărcări Totale',
      value: '2,145',
      change: '+189 această lună',
      changeType: 'positive',
      icon: Download,
      color: 'info'
    }
  ];

  const filteredStandards = standards.filter(standard => {
    const matchesCategory = selectedCategory === 'toate' || standard.category === selectedCategory;
    const matchesRegion = selectedRegion === 'toate' || standard.region === selectedRegion;
    const matchesSearch = standard.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         standard.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         standard.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         standard.applicableFor.some(item => item.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesRegion && matchesSearch;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'Activă': return 'success';
      case 'În revizuire': return 'warning';
      case 'Retrasă': return 'error';
      default: return 'gray';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'Ridicată': return 'error';
      case 'Medie': return 'warning';
      case 'Scăzută': return 'success';
      default: return 'gray';
    }
  };

  const getComplexityColor = (complexity) => {
    switch(complexity) {
      case 'Complexă': return 'error';
      case 'Medie': return 'warning';
      case 'Ușoară': return 'success';
      default: return 'gray';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('ro-RO', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const stars = [];
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="w-3 h-3 text-yellow-400 fill-current" />);
    }
    if (hasHalfStar) {
      stars.push(<Star key="half" className="w-3 h-3 text-yellow-400 fill-current opacity-50" />);
    }
    return stars;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <BookOpen className="w-8 h-8 mr-3 text-primary-600" />
            Biblioteca Standarde & Norme
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Standarde românești și europene pentru pardoseli industriale - {filteredStandards.length} standarde disponibile
          </p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <button className="btn btn-secondary">
            <ExternalLink className="w-4 h-4 mr-2" />
            ASRO Online
          </button>
          
          <button className="btn btn-primary">
            <Search className="w-4 h-4 mr-2" />
            Căutare Avansată
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="relative overflow-hidden">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
                  <p className={`text-sm mt-1 ${
                    stat.changeType === 'positive' ? 'text-success' :
                    stat.changeType === 'negative' ? 'text-error' : 
                    'text-gray-500 dark:text-gray-400'
                  }`}>
                    {stat.change}
                  </p>
                </div>
                <div className={`p-3 rounded-xl bg-${stat.color}-100 dark:bg-${stat.color}-900/30`}>
                  <Icon className={`w-6 h-6 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Filters Sidebar */}
        <div className="space-y-6">
          {/* Search */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Căutare</h3>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Caută standarde..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
              />
            </div>
          </Card>

          {/* Categories */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Categorii</h3>
              <Filter className="w-4 h-4 text-gray-400" />
            </div>
            
            <div className="space-y-2">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center">
                      <Icon className="w-4 h-4 mr-2" />
                      <span className="text-sm font-medium">{category.name}</span>
                    </div>
                    <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full">
                      {category.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Regions */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Organisme</h3>
            
            <div className="space-y-2">
              {regions.map((region) => (
                <button
                  key={region.id}
                  onClick={() => setSelectedRegion(region.id)}
                  className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors ${
                    selectedRegion === region.id
                      ? 'bg-secondary-100 dark:bg-secondary-900/30 text-secondary-700 dark:text-secondary-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className="text-sm">{region.name}</span>
                  <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full">
                    {region.count}
                  </span>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Standards List */}
        <div className="xl:col-span-3">
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Standarde ({filteredStandards.length})
              </h3>
              <div className="flex items-center space-x-2">
                <select className="form-select text-sm">
                  <option value="relevance">Relevanță</option>
                  <option value="date">Dată publicare</option>
                  <option value="title">Titlu A-Z</option>
                  <option value="downloads">Descărcări</option>
                  <option value="rating">Rating</option>
                </select>
              </div>
            </div>
            
            <div className="space-y-6">
              {filteredStandards.map((standard, index) => (
                <div key={index} className="p-6 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{standard.title}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getStatusColor(standard.status)}-100 text-${getStatusColor(standard.status)}-700 dark:bg-${getStatusColor(standard.status)}-900/30 dark:text-${getStatusColor(standard.status)}-300`}>
                          {standard.status}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getPriorityColor(standard.priority)}-100 text-${getPriorityColor(standard.priority)}-700 dark:bg-${getPriorityColor(standard.priority)}-900/30 dark:text-${getPriorityColor(standard.priority)}-300`}>
                          {standard.priority}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getComplexityColor(standard.complexity)}-100 text-${getComplexityColor(standard.complexity)}-700 dark:bg-${getComplexityColor(standard.complexity)}-900/30 dark:text-${getComplexityColor(standard.complexity)}-300`}>
                          {standard.complexity}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{standard.fullName}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{standard.description}</p>
                    </div>
                    
                    <div className="flex items-center ml-6">
                      <div className="text-right mr-4">
                        <div className="flex items-center mb-1">
                          {renderStars(standard.rating)}
                          <span className="text-sm text-gray-500 dark:text-gray-500 ml-1">({standard.rating})</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-500">
                          <span className="flex items-center">
                            <Download className="w-3 h-3 mr-1" />
                            {standard.downloads}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-900 dark:text-white">Organizație:</span>
                      <p className="text-gray-600 dark:text-gray-400">{standard.organization}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 dark:text-white">Publicată:</span>
                      <p className="text-gray-600 dark:text-gray-400">{formatDate(standard.publishDate)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 dark:text-white">Actualizată:</span>
                      <p className="text-gray-600 dark:text-gray-400">{formatDate(standard.lastUpdate)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 dark:text-white">Pagini:</span>
                      <p className="text-gray-600 dark:text-gray-400">{standard.pages} pag. • {standard.language}</p>
                    </div>
                  </div>

                  {/* Applicable For */}
                  <div className="mb-4">
                    <span className="text-sm font-medium text-gray-900 dark:text-white mb-2 block">Aplicabilă pentru:</span>
                    <div className="flex flex-wrap gap-2">
                      {standard.applicableFor.map((item, itemIndex) => (
                        <span key={itemIndex} className="px-3 py-1 text-xs bg-info-100 dark:bg-info-900/30 text-info-700 dark:text-info-300 rounded-full">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Key Points */}
                  <div className="mb-4">
                    <span className="text-sm font-medium text-gray-900 dark:text-white mb-2 block">Puncte cheie:</span>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      {standard.keyPoints.map((point, pointIndex) => (
                        <li key={pointIndex} className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-success mr-2 mt-0.5 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Related Standards */}
                  <div className="mb-4">
                    <span className="text-sm font-medium text-gray-900 dark:text-white mb-2 block">Standarde conexe:</span>
                    <div className="flex flex-wrap gap-2">
                      {standard.relatedStandards.map((related, relIndex) => (
                        <button key={relIndex} className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                          {related}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-500">
                      <span className="flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        {standard.bookmarks} favorite
                      </span>
                      <span className="flex items-center">
                        <Download className="w-4 h-4 mr-1" />
                        {standard.downloads} descărcări
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button className="p-2 text-gray-600 hover:text-warning-600 hover:bg-warning-100 dark:hover:bg-warning-900/30 rounded-lg transition-colors">
                        <Star className="w-4 h-4" />
                      </button>
                      <button className="btn btn-sm btn-secondary">
                        <Eye className="w-3 h-3 mr-1" />
                        Previzualizare
                      </button>
                      <button className="btn btn-sm btn-primary">
                        <Download className="w-3 h-3 mr-1" />
                        Descarcă PDF
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default StandardsLibrary;